import { Component, OnDestroy, OnInit } from '@angular/core';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { Subject, takeUntil } from 'rxjs';
import { UserRole } from '../../models/user';
import {
  ACTIVITY_BUCKET_LABELS,
  ActivityBucket,
  ActivityTotals,
  TenantRollup,
  UsageResponse,
  UsageUserVM
} from '../../models/usage';
import { UsageService } from '../../../services/usage.service';
import {
  formatRelative, lastActiveTooltip,
  userRoleClass, userRoleLabel, userStatusClass, userStatusLabel
} from '../../utils/user-display';

const DAY_MS = 86_400_000;
/** Beyond this many tenants the per-tenant chart stops being readable and the tail is grouped. */
const CHART_TENANT_LIMIT = 10;

@Component({
  selector: 'app-usage',
  templateUrl: './usage.component.html',
  styleUrls: ['./usage.component.scss']
})
export class UsageComponent implements OnInit, OnDestroy {
  loading = true;
  refreshing = false;
  error: string | null = null;

  generatedAt: string | null = null;
  /** False when Keycloak could not resolve activity at all — see the template banner. */
  lastActiveAvailable = true;

  users: UsageUserVM[] = [];
  filteredUsers: UsageUserVM[] = [];
  rollups: TenantRollup[] = [];
  totals: ActivityTotals = { users: 0, activeToday: 0, activeWeek: 0, activeMonth: 0, never: 0 };

  // Filters. All of these run over the already-loaded array — there is no request behind them,
  // so the input can never be disabled or unmounted mid-keystroke.
  searchTerm = '';
  tenantFilter = '';
  roleFilter = '';
  statusFilter: '' | 'enabled' | 'disabled' = '';
  bucketFilter: '' | ActivityBucket = '';

  tenantOptions: { id: number; name: string }[] = [];
  roleOptions: string[] = [];
  bucketOptions = Object.entries(ACTIVITY_BUCKET_LABELS) as [ActivityBucket, string][];

  // Chart data
  tenantChartData: { name: string; series: { name: string; value: number }[] }[] = [];
  bucketChartData: { name: string; value: number }[] = [];
  chartTailNote: string | null = null;

  readonly activeSeriesLabel = 'Active (last 30 days)';
  readonly inactiveSeriesLabel = 'Inactive';

  /**
   * Blue/orange. Validated for colour-vision deficiency rather than picked by eye — the pair
   * separates at ΔE 27+ under protanopia and deuteranopia. Every bar also carries its count in
   * text and the tables below repeat the numbers, so colour never carries meaning alone.
   */
  readonly tenantScheme: Color = {
    name: 'usageTenant',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#2f7fd0', '#f0932b']
  };

  /** One series, so one hue — the bucket names on the axis carry the identity. */
  readonly bucketScheme: Color = {
    name: 'usageBucket',
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#2f7fd0']
  };

  private destroy$ = new Subject<void>();

  constructor(private usageService: UsageService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  load(refresh = false): void {
    this.refreshing = true;
    this.error = null;

    this.usageService.getUsage(refresh).pipe(takeUntil(this.destroy$)).subscribe({
      next: response => {
        this.apply(response);
        this.loading = false;
        this.refreshing = false;
      },
      error: error => {
        console.error('Error loading usage:', error);
        this.error = 'Failed to load usage data. Please try again.';
        this.loading = false;
        this.refreshing = false;
      }
    });
  }

  refresh(): void {
    this.load(true);
  }

  private apply(response: UsageResponse): void {
    this.generatedAt = response.generatedAt;
    this.lastActiveAvailable = response.lastActiveAvailable;

    // Local midnight, not UTC: "today" is a calendar question, and the only clock that gives the
    // admin the answer they expect is the one in front of them. The 7/30-day windows roll from
    // now instead — the tiles say so, because mixing the two silently would mislead.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const todayMs = startOfToday.getTime();
    const now = Date.now();
    const since7 = now - 7 * DAY_MS;
    const since30 = now - 30 * DAY_MS;

    const tenantNames = new Map<number, string>();
    response.tenants.forEach(tenant => tenantNames.set(tenant.id, tenant.name || `Tenant ${tenant.id}`));

    this.users = (response.data || []).map(user => {
      const lastActiveMs = this.parseMs(user.lastActiveAt);
      const tenantLabel = user.tenantId != null
        ? (tenantNames.get(user.tenantId) || `Tenant ${user.tenantId}`)
        : 'Unknown';

      return {
        ...user,
        tenantLabel,
        roleLabel: userRoleLabel(user.role),
        roleClass: userRoleClass(user.role),
        statusLabel: userStatusLabel(user.enabled),
        statusClass: userStatusClass(user.enabled),
        lastActiveText: formatRelative(user.lastActiveAt),
        lastActiveTitle: lastActiveTooltip(user),
        lastActiveMs,
        bucket: this.bucketFor(lastActiveMs, todayMs, since7, since30)
      };
    });

    this.totals = {
      users: this.users.length,
      activeToday: this.users.filter(u => u.lastActiveMs !== null && u.lastActiveMs >= todayMs).length,
      activeWeek: this.users.filter(u => u.lastActiveMs !== null && u.lastActiveMs >= since7).length,
      activeMonth: this.users.filter(u => u.lastActiveMs !== null && u.lastActiveMs >= since30).length,
      never: this.users.filter(u => u.lastActiveMs === null).length
    };

    this.buildRollups(response, tenantNames, since7, since30);
    this.buildCharts();

    this.tenantOptions = response.tenants
      .map(tenant => ({ id: tenant.id, name: tenantNames.get(tenant.id)! }))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.roleOptions = Array.from(new Set(this.users.map(u => u.role).filter(Boolean) as UserRole[])).sort();

    this.applyFilters();
  }

  private buildRollups(response: UsageResponse, tenantNames: Map<number, string>,
                       since7: number, since30: number): void {
    const byTenant = new Map<number, UsageUserVM[]>();
    this.users.forEach(user => {
      if (user.tenantId == null) {
        return;
      }
      const bucket = byTenant.get(user.tenantId) ?? [];
      bucket.push(user);
      byTenant.set(user.tenantId, bucket);
    });

    this.rollups = response.tenants.map(tenant => {
      const tenantUsers = byTenant.get(tenant.id) ?? [];
      const timestamps = tenantUsers.map(u => u.lastActiveMs).filter((ms): ms is number => ms !== null);
      const mostRecentMs = timestamps.length ? Math.max(...timestamps) : null;

      return {
        id: tenant.id,
        name: tenantNames.get(tenant.id)!,
        groupExists: tenant.groupExists,
        users: tenantUsers.length,
        activeWeek: tenantUsers.filter(u => u.lastActiveMs !== null && u.lastActiveMs >= since7).length,
        activeMonth: tenantUsers.filter(u => u.lastActiveMs !== null && u.lastActiveMs >= since30).length,
        mostRecentMs,
        mostRecentText: mostRecentMs === null ? 'Never' : formatRelative(new Date(mostRecentMs).toISOString())
      };
    }).sort((a, b) => b.activeMonth - a.activeMonth || b.users - a.users || a.name.localeCompare(b.name));
  }

  private buildCharts(): void {
    const shown = this.rollups.slice(0, CHART_TENANT_LIMIT);
    const tail = this.rollups.slice(CHART_TENANT_LIMIT);

    this.tenantChartData = shown.map(rollup => ({
      name: rollup.name,
      series: [
        { name: this.activeSeriesLabel, value: rollup.activeMonth },
        { name: this.inactiveSeriesLabel, value: rollup.users - rollup.activeMonth }
      ]
    }));

    if (tail.length) {
      const users = tail.reduce((sum, r) => sum + r.users, 0);
      const active = tail.reduce((sum, r) => sum + r.activeMonth, 0);
      this.tenantChartData.push({
        name: `${tail.length} more tenants`,
        series: [
          { name: this.activeSeriesLabel, value: active },
          { name: this.inactiveSeriesLabel, value: users - active }
        ]
      });
      // Never let a chart quietly drop rows — say what was folded up.
      this.chartTailNote = `Top ${CHART_TENANT_LIMIT} tenants shown; the remaining ${tail.length} are grouped.`;
    } else {
      this.chartTailNote = null;
    }

    // Exclusive buckets, so the bars sum to the total user count — unlike the tiles above,
    // which are cumulative. The labels spell out the difference.
    this.bucketChartData = this.bucketOptions.map(([bucket, label]) => ({
      name: label,
      value: this.users.filter(u => u.bucket === bucket).length
    }));
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();

    this.filteredUsers = this.users.filter(user => {
      if (term && !`${user.name} ${user.email ?? ''} ${user.tenantLabel}`.toLowerCase().includes(term)) {
        return false;
      }
      if (this.tenantFilter && String(user.tenantId) !== this.tenantFilter) {
        return false;
      }
      if (this.roleFilter && user.role !== this.roleFilter) {
        return false;
      }
      if (this.statusFilter && (this.statusFilter === 'enabled') !== user.enabled) {
        return false;
      }
      if (this.bucketFilter && user.bucket !== this.bucketFilter) {
        return false;
      }
      return true;
    });
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.tenantFilter = '';
    this.roleFilter = '';
    this.statusFilter = '';
    this.bucketFilter = '';
    this.applyFilters();
  }

  percentOf(count: number): string {
    return this.totals.users === 0 ? '—' : `${Math.round((count / this.totals.users) * 100)}% of all users`;
  }

  trackByUserId(index: number, user: UsageUserVM): string {
    return user.id ?? String(index);
  }

  trackByTenantId(index: number, rollup: TenantRollup): number {
    return rollup.id ?? index;
  }

  roleLabelFor(role: string): string {
    return userRoleLabel(role);
  }

  private bucketFor(ms: number | null, todayMs: number, since7: number, since30: number): ActivityBucket {
    if (ms === null) {
      return 'never';
    }
    if (ms >= todayMs) {
      return 'today';
    }
    if (ms >= since7) {
      return 'week';
    }
    if (ms >= since30) {
      return 'month';
    }
    return 'older';
  }

  private parseMs(iso?: string | null): number | null {
    if (!iso) {
      return null;
    }
    const ms = new Date(iso).getTime();
    return isNaN(ms) ? null : ms;
  }
}
